import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AuthImage } from "@/components/common/AuthImage";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { DefectDetailDialog } from "@/components/defects/DefectDetailDialog";
import { DEFECT_STATUS_META, DEFECT_PRIORITY_META } from "@/components/defects/defectMeta";

/**
 * Contractor portal: every defect assigned to the signed-in trade (matched by email) across their jobs.
 * Open one to mark it started / rectified with evidence photos — same workflow as the emailed secure link.
 */
export const ContractorDefectsCard = () => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback(() => {
    api.get("/contractor/defects").then((r) => { setData(r.data); setFailed(false); }).catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (failed) return null;
  if (!data) return <Skeleton className="h-28 w-full" data-testid="contractor-defects-loading" />;

  const rows = data.rows || [];
  const open = rows.filter((r) => !r.resolved);
  const resolved = rows.filter((r) => r.resolved);
  if (!rows.length) return null;
  const shown = showResolved ? rows : open;

  return (
    <Card className={`shadow-sm ${open.length ? "border-l-2 border-l-primary" : ""}`} data-testid="contractor-defects-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          <ClipboardList className="h-4 w-4 text-primary" /> Defects assigned to you
          {open.length > 0 && <Badge variant="outline" className="border-primary/40 bg-accent text-primary" data-testid="contractor-defects-open-count">{open.length} open</Badge>}
          {data.overdue > 0 && <Badge variant="outline" className="border-rose-500/40 bg-rose-500/10 text-rose-300" data-testid="contractor-defects-overdue-count">{data.overdue} overdue</Badge>}
          {data.awaiting_verification > 0 && <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-300" data-testid="contractor-defects-verify-count">{data.awaiting_verification} awaiting builder check</Badge>}
          {resolved.length > 0 && (
            <button type="button" className="ml-auto text-xs font-normal text-muted-foreground underline-offset-2 hover:underline" onClick={() => setShowResolved((v) => !v)} data-testid="contractor-defects-toggle-resolved">
              {showResolved ? "Hide resolved" : `Show ${resolved.length} resolved`}
            </button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {shown.length === 0 ? (
          <p className="py-3 text-sm text-muted-foreground" data-testid="contractor-defects-empty">Nothing outstanding — every defect assigned to you has been verified or closed.</p>
        ) : (
          <ul className="divide-y divide-border">
            {shown.map((r) => {
              const sm = DEFECT_STATUS_META[r.status] || DEFECT_STATUS_META.open;
              const pm = DEFECT_PRIORITY_META[r.priority] || DEFECT_PRIORITY_META.major;
              return (
                <li key={r.id}>
                  <button type="button" className="flex w-full items-center gap-3 py-2.5 text-left transition-colors duration-150 hover:bg-muted/30" onClick={() => setDetailId(r.id)} data-testid={`contractor-defect-${r.number}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-xs font-semibold">{r.number}</span>
                        <Badge variant="outline" className={`h-5 font-normal ${pm.cls}`}>{pm.label}</Badge>
                        <Badge variant="outline" className={`h-5 font-normal ${sm.cls}`}>{sm.short}</Badge>
                        {r.overdue && <Badge variant="outline" className="h-5 border-rose-500/40 bg-rose-500/10 font-normal text-rose-300">Overdue</Badge>}
                      </div>
                      <p className="mt-0.5 line-clamp-1 text-sm">{r.description}</p>
                      <p className="text-[11px] text-muted-foreground">
                        <Link to={`/jobs/${r.job_id}?tab=defects`} className="font-medium text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{r.job_name}</Link>
                        {" · "}{r.location_label} · due {r.due_date ? fmtDate(r.due_date) : "—"}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <DefectDetailDialog open={Boolean(detailId)} onOpenChange={(v) => !v && setDetailId(null)} defectId={detailId} onChanged={load} onPhotoOpen={setLightbox} />
      <Dialog open={Boolean(lightbox)} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2" data-testid="contractor-defects-lightbox">
          {lightbox && <AuthImage path={lightbox.path} alt={lightbox.caption || "Defect photo"} className="max-h-[75vh] w-full rounded-lg object-contain" testId="contractor-defects-lightbox-img" />}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
